<script>
import draggable from 'vuedraggable';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import StageRequirementsPanel from './StageRequirementsPanel.vue';

export default {
  name: 'KanbanSettingsModal',
  components: {
    Draggable: draggable,
    NextButton,
    StageRequirementsPanel,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    funnels: {
      type: Array,
      default: () => [],
    },
    activeFunnelId: {
      type: String,
      default: null,
    },
    isDealsMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeTab: 'funnels', // 'funnels', 'lost_reasons', 'payment_methods', 'origins'

      // Funnels state
      viewMode: 'list', // list, form
      isEditing: false,
      editingFunnelId: null,
      funnelName: '',
      selectedLabels: [],
      // Modo `deals`, criação: sem etiquetas, a pessoa digita os nomes das etapas.
      stageNames: [],
      // Modo `deals`, edição: etapas de verdade (com id), uma por linha.
      editingStages: [],
      // Qual etapa está com o painel de requisitos aberto (uma por vez).
      requirementsStageId: null,
      showWinRequirements: false,
      // Prazo de destaque ("parado há mais de N horas/dias") sendo editado.
      staleStageId: null,
      staleValue: 1,
      staleUnit: 'days',
      newStageName: '',
      editingStageIndex: null,
      editingStageValue: '',

      // CRM Lists state
      activeList: [],
      newItemName: '',
      hasChanges: false,
      editingIndex: null,
      editingValue: '',
    };
  },
  computed: {
    // O funil sendo editado, com os requisitos de ganho que o servidor gravou.
    editingFunnel() {
      const funnels = this.$store.getters['crmDeals/getFunnels'] || [];
      return funnels.find(f => f.id === this.editingFunnelId) || null;
    },
    winRequirementCount() {
      const requirements = this.editingFunnel?.win_requirements || {};
      return (
        (requirements.fields || []).length +
        (requirements.checklist || []).length
      );
    },
    allLabels() {
      return this.$store.getters['labels/getLabels'] || [];
    },
    availableLabels() {
      const selectedTitles = this.selectedLabels.map(l => l.title);
      return this.allLabels.filter(
        label => !selectedTitles.includes(label.title)
      );
    },
    isFormValid() {
      if (this.isDealsMode && !this.isEditing) {
        return (
          this.funnelName.trim().length > 0 &&
          this.stageNames.some(name => name.trim().length > 0)
        );
      }
      if (this.isDealsMode && this.isEditing) {
        return this.funnelName.trim().length > 0;
      }
      return (
        this.funnelName.trim().length > 0 && this.selectedLabels.length > 0
      );
    },
    listConfig() {
      if (this.activeTab === 'lost_reasons') {
        return {
          title: 'Motivos de Perda',
          description:
            'Crie ou edite as opções que aparecerão quando um negócio for marcado como Perdido.',
          placeholder: 'Ex: Preço, Concorrente, Sem contato...',
        };
      }
      if (this.activeTab === 'origins') {
        return {
          title: 'Origens do Lead',
          description:
            'Crie ou edite as origens disponíveis para identificar de onde veio cada lead.',
          placeholder: 'Ex: Google Ads, Indicação, Instagram...',
        };
      }
      return {
        title: 'Formas de Pagamento',
        description:
          'Crie ou edite as formas de pagamento disponíveis quando um negócio for Ganho.',
        placeholder: 'Ex: Pix, Cartão de Crédito...',
      };
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.activeTab = 'funnels';
        this.viewMode = 'list';
        this.$store.dispatch('labels/get');
      }
    },
    activeTab(newVal) {
      if (newVal !== 'funnels') {
        this.loadActiveList();
      }
    },
  },
  methods: {
    emitSelectFunnel(id) {
      this.$emit('select-funnel', id);
      this.onClose();
    },
    openCreateMode() {
      this.isEditing = false;
      this.editingFunnelId = null;
      this.funnelName = '';
      this.selectedLabels = [];
      this.stageNames = ['Novo', 'Proposta', 'Fechado'];
      this.viewMode = 'form';
    },
    openEditMode(funnel) {
      this.isEditing = true;
      this.editingFunnelId = funnel.id;
      this.funnelName = funnel.name;

      if (this.isDealsMode) {
        this.newStageName = '';
        this.editingStageIndex = null;
        this.loadDealsStages(funnel.id);
        this.viewMode = 'form';
        return;
      }

      this.selectedLabels = funnel.columns.map(colRef => {
        const colRefNum = Number(colRef);
        const matchById = this.allLabels.find(
          l => l.id === colRefNum || l.id === colRef
        );
        if (matchById) return matchById;
        const matchByTitle = this.allLabels.find(l => l.title === colRef);
        return matchByTitle || { id: colRef, title: colRef, color: '#ccc' };
      });
      this.viewMode = 'form';
    },
    addLabel(event) {
      const labelId = Number(event.target.value);
      const label = this.allLabels.find(l => l.id === labelId);
      if (label) {
        this.selectedLabels.push(label);
      }
      event.target.value = '';
    },
    removeLabel(labelToRemove) {
      this.selectedLabels = this.selectedLabels.filter(
        label => label.id !== labelToRemove.id
      );
    },
    async saveFunnel() {
      if (!this.isFormValid) return;

      if (this.isDealsMode && !this.isEditing) {
        const funnelData = {
          name: this.funnelName.trim(),
          stages: this.stageNames.map(s => s.trim()).filter(Boolean),
        };
        this.$emit('save-funnel', funnelData);
        this.viewMode = 'list';
        return;
      }

      if (this.isDealsMode && this.isEditing) {
        try {
          await this.$store.dispatch('crmDeals/updateFunnel', {
            id: this.editingFunnelId,
            name: this.funnelName.trim(),
          });
          useAlert('Funil atualizado com sucesso!');
        } catch (error) {
          useAlert(this.crmErrorMessage(error, 'Erro ao salvar o funil.'));
        }
        this.viewMode = 'list';
        return;
      }

      const funnelData = {
        name: this.funnelName.trim(),
        columns: this.selectedLabels.map(l => l.id),
        id: this.isEditing ? this.editingFunnelId : Date.now().toString(),
      };
      this.$emit('save-funnel', funnelData);
      this.viewMode = 'list';
    },
    addStageField() {
      this.stageNames.push('');
    },
    removeStageField(index) {
      this.stageNames.splice(index, 1);
    },
    async deleteFunnel(id) {
      if (!confirm('Tem certeza que deseja excluir este funil?')) return;

      if (this.isDealsMode) {
        try {
          await this.$store.dispatch('crmDeals/deleteFunnel', id);
          this.$emit('delete-funnel', id);
        } catch (error) {
          useAlert(this.crmErrorMessage(error, 'Erro ao excluir o funil.'));
        }
        return;
      }

      this.$emit('delete-funnel', id);
    },

    // Modo `deals`: mensagem de erro do servidor (ex.: 422 de funil/etapa com
    // negócio) diz o que fazer — nunca troque por um erro genérico.
    crmErrorMessage(error, fallback) {
      return error?.response?.data?.message || fallback;
    },
    loadDealsStages(funnelId) {
      const fullFunnel = (
        this.$store.getters['crmDeals/getFunnels'] || []
      ).find(f => String(f.id) === String(funnelId));
      this.editingStages = fullFunnel
        ? [...fullFunnel.stages].sort((a, b) => a.position - b.position)
        : [];
    },
    async addDealStage() {
      const name = this.newStageName.trim();
      if (!name) return;
      const nextPosition =
        this.editingStages.length > 0
          ? Math.max(...this.editingStages.map(s => s.position)) + 1
          : 1;
      try {
        await this.$store.dispatch('crmDeals/createStage', {
          funnelId: this.editingFunnelId,
          stage: { name, position: nextPosition },
        });
        this.newStageName = '';
        this.loadDealsStages(this.editingFunnelId);
      } catch (error) {
        useAlert(this.crmErrorMessage(error, 'Erro ao criar a etapa.'));
      }
    },
    startEditStage(index) {
      this.editingStageIndex = index;
      this.editingStageValue = this.editingStages[index].name;
    },
    async saveEditStage(index) {
      const name = this.editingStageValue.trim();
      if (!name) return;
      const stage = this.editingStages[index];
      try {
        await this.$store.dispatch('crmDeals/updateStage', {
          funnelId: this.editingFunnelId,
          stageId: stage.id,
          stage: { name },
        });
        this.editingStageIndex = null;
        this.loadDealsStages(this.editingFunnelId);
      } catch (error) {
        useAlert(this.crmErrorMessage(error, 'Erro ao renomear a etapa.'));
      }
    },
    cancelEditStage() {
      this.editingStageIndex = null;
      this.editingStageValue = '';
    },
    // Prazo gravado em horas; a tela mostra em dias quando fecha redondo.
    staleLabel(stage) {
      const hours = stage.stale_after_hours;
      if (!hours) return '';
      return hours % 24 === 0 ? `${hours / 24}d` : `${hours}h`;
    },
    toggleStale(stage) {
      if (this.staleStageId === stage.id) {
        this.staleStageId = null;
        return;
      }
      const hours = stage.stale_after_hours || 24;
      this.staleUnit = hours % 24 === 0 ? 'days' : 'hours';
      this.staleValue = this.staleUnit === 'days' ? hours / 24 : hours;
      this.staleStageId = stage.id;
    },
    async saveStale(stage, clear = false) {
      const value = Number(this.staleValue);
      if (!clear && (!Number.isInteger(value) || value <= 0)) {
        useAlert('Informe um número inteiro maior que zero.');
        return;
      }
      const hours = clear
        ? null
        : value * (this.staleUnit === 'days' ? 24 : 1);
      try {
        await this.$store.dispatch('crmDeals/updateStage', {
          funnelId: this.editingFunnelId,
          stageId: stage.id,
          stage: { stale_after_hours: hours },
        });
        this.staleStageId = null;
        this.loadDealsStages(this.editingFunnelId);
      } catch (error) {
        useAlert(this.crmErrorMessage(error, 'Erro ao salvar o prazo.'));
      }
    },
    requirementCount(stage) {
      const requirements = stage.requirements || {};
      return (
        (requirements.fields || []).length +
        (requirements.checklist || []).length
      );
    },
    toggleRequirements(stage) {
      this.requirementsStageId =
        this.requirementsStageId === stage.id ? null : stage.id;
    },
    async onWinRequirementsSaved() {
      this.showWinRequirements = false;
      await this.$store.dispatch('crmDeals/fetchFunnels');
    },
    // Recarrega os funis pra badge de contagem refletir o que acabou de ser
    // salvo sem precisar reabrir o modal.
    async onRequirementsSaved() {
      this.requirementsStageId = null;
      await this.$store.dispatch('crmDeals/fetchFunnels');
      const funnel = this.$store.getters['crmDeals/getFunnels'].find(
        item => String(item.id) === String(this.editingFunnelId)
      );
      if (funnel) {
        this.editingStages = [...funnel.stages].sort(
          (a, b) => a.position - b.position
        );
      }
    },
    async deleteDealStage(stage) {
      if (!confirm('Tem certeza que deseja excluir esta etapa?')) return;
      try {
        await this.$store.dispatch('crmDeals/deleteStage', {
          funnelId: this.editingFunnelId,
          stageId: stage.id,
        });
        this.loadDealsStages(this.editingFunnelId);
      } catch (error) {
        useAlert(this.crmErrorMessage(error, 'Erro ao excluir a etapa.'));
      }
    },
    async reorderDealStages() {
      const updates = this.editingStages
        .map((stage, index) => ({ stage, position: index + 1 }))
        .filter(({ stage, position }) => stage.position !== position);

      if (!updates.length) return;

      try {
        await Promise.all(
          updates.map(({ stage, position }) =>
            this.$store.dispatch('crmDeals/updateStage', {
              funnelId: this.editingFunnelId,
              stageId: stage.id,
              stage: { position },
            })
          )
        );
      } catch (error) {
        useAlert(this.crmErrorMessage(error, 'Erro ao reordenar as etapas.'));
      } finally {
        this.loadDealsStages(this.editingFunnelId);
      }
    },

    // CRM List Management Methods
    // Modo `deals`: cada aba (origens/formas de pagamento/motivos de perda)
    // larga custom_attributes e passa a gravar direto na API — sem lote,
    // sem botão "Salvar Alterações": cada ação já é persistida na hora.
    dealsListActionSuffix() {
      return {
        lost_reasons: 'LostReason',
        payment_methods: 'PaymentMethod',
        origins: 'Origin',
      }[this.activeTab];
    },
    dealsListGetter() {
      if (this.activeTab === 'lost_reasons')
        return this.$store.getters['crmDeals/getLostReasons'];
      if (this.activeTab === 'payment_methods')
        return this.$store.getters['crmDeals/getPaymentMethods'];
      return this.$store.getters['crmDeals/getOrigins'];
    },
    // Nome da chave que a action `update*` espera dentro do payload —
    // difere por recurso (ver crmDeals.js: updateOrigin/updatePaymentMethod/updateLostReason).
    dealsListParamKey() {
      return {
        lost_reasons: 'lostReason',
        payment_methods: 'paymentMethod',
        origins: 'origin',
      }[this.activeTab];
    },
    async loadDealsList() {
      const fetchAction = {
        lost_reasons: 'crmDeals/fetchLostReasons',
        payment_methods: 'crmDeals/fetchPaymentMethods',
        origins: 'crmDeals/fetchOrigins',
      }[this.activeTab];
      await this.$store.dispatch(fetchAction);
      this.activeList = [...this.dealsListGetter()];
    },
    loadActiveList() {
      this.newItemName = '';
      this.hasChanges = false;

      if (this.isDealsMode) {
        this.loadDealsList();
        return;
      }

      const accountId = this.$store.getters.getCurrentAccountId;
      const account = this.$store.getters['accounts/getAccount'](accountId);
      const customAttributes = account?.custom_attributes || {};

      let items = [];
      if (this.activeTab === 'lost_reasons') {
        items = customAttributes.crm_lost_reasons;
        if (!items || items.length === 0) {
          items = [
            'Preço',
            'Sem interesse',
            'Fechou com concorrente',
            'Parou de responder',
            'Outros',
          ];
          this.hasChanges = true; // Auto-save trigger if they hit save
        }
      } else if (this.activeTab === 'payment_methods') {
        items = customAttributes.crm_payment_methods;
        if (!items || items.length === 0) {
          items = [
            'Pix',
            'Cartão de Crédito',
            'Boleto',
            'Transferência Bancária',
          ];
          this.hasChanges = true;
        }
      } else if (this.activeTab === 'origins') {
        items = customAttributes.crm_origins;
        if (!items || items.length === 0) {
          items = [
            'WhatsApp',
            'Instagram',
            'Google Ads',
            'Indicação',
            'Website',
          ];
          this.hasChanges = true;
        }
      }
      this.activeList = [...items];
    },
    async addListItem() {
      const name = this.newItemName.trim();
      if (!name) return;

      if (this.isDealsMode) {
        try {
          await this.$store.dispatch(
            `crmDeals/create${this.dealsListActionSuffix()}`,
            { name }
          );
          this.activeList = [...this.dealsListGetter()];
          this.newItemName = '';
        } catch (error) {
          useAlert(this.crmErrorMessage(error, 'Erro ao criar o item.'));
        }
        return;
      }

      this.activeList.push(name);
      this.newItemName = '';
      this.hasChanges = true;
    },
    async removeListItem(index) {
      if (this.isDealsMode) {
        if (!confirm('Tem certeza que deseja excluir este item?')) return;
        const item = this.activeList[index];
        try {
          await this.$store.dispatch(
            `crmDeals/delete${this.dealsListActionSuffix()}`,
            item.id
          );
          this.activeList = [...this.dealsListGetter()];
        } catch (error) {
          useAlert(this.crmErrorMessage(error, 'Erro ao excluir o item.'));
        }
        this.editingIndex = null;
        return;
      }

      this.activeList.splice(index, 1);
      this.hasChanges = true;
      this.editingIndex = null;
    },
    startEdit(index) {
      this.editingIndex = index;
      const item = this.activeList[index];
      this.editingValue = this.isDealsMode ? item.name : item;
    },
    async saveEdit(index) {
      const name = this.editingValue.trim();
      if (!name) return;

      if (this.isDealsMode) {
        const item = this.activeList[index];
        try {
          await this.$store.dispatch(
            `crmDeals/update${this.dealsListActionSuffix()}`,
            { id: item.id, [this.dealsListParamKey()]: { name } }
          );
          this.activeList = [...this.dealsListGetter()];
        } catch (error) {
          useAlert(this.crmErrorMessage(error, 'Erro ao renomear o item.'));
        }
        this.editingIndex = null;
        this.editingValue = '';
        return;
      }

      this.activeList.splice(index, 1, name);
      this.hasChanges = true;
      this.editingIndex = null;
      this.editingValue = '';
    },
    cancelEdit() {
      this.editingIndex = null;
      this.editingValue = '';
    },
    async saveList() {
      const keyMap = {
        lost_reasons: 'crm_lost_reasons',
        payment_methods: 'crm_payment_methods',
        origins: 'crm_origins',
      };
      const attributeKey = keyMap[this.activeTab];
      const accountId = this.$store.getters.getCurrentAccountId;
      const account = this.$store.getters['accounts/getAccount'](accountId);

      try {
        await this.$store.dispatch('accounts/update', {
          custom_attributes: {
            ...account.custom_attributes,
            [attributeKey]: this.activeList,
          },
        });
        this.hasChanges = false;
        useAlert('Configurações salvas com sucesso!');
      } catch (error) {
        useAlert('Erro ao salvar configurações.');
      }
    },

    onClose() {
      this.$emit('close');
    },
  },
};
</script>

<template>
  <woot-modal :show="show" :on-close="onClose">
    <div
      class="flex flex-col h-[80vh] min-h-[500px] max-h-[800px] bg-white dark:bg-slate-900 overflow-hidden"
    >
      <!-- Top Navigation Tabs -->
      <div
        class="px-6 border-b border-n-weak flex justify-between items-center bg-n-surface-1"
      >
        <div class="flex gap-4">
          <button
            class="px-4 py-4 text-sm font-semibold border-b-2 transition-colors cursor-pointer"
            :class="
              activeTab === 'funnels'
                ? 'border-n-brand text-n-brand'
                : 'border-transparent text-n-slate-11 hover:text-slate-800 dark:hover:text-slate-100'
            "
            @click="activeTab = 'funnels'"
          >
            Funis
          </button>
          <button
            class="px-4 py-4 text-sm font-semibold border-b-2 transition-colors cursor-pointer"
            :class="
              activeTab === 'lost_reasons'
                ? 'border-n-brand text-n-brand'
                : 'border-transparent text-n-slate-11 hover:text-slate-800 dark:hover:text-slate-100'
            "
            @click="activeTab = 'lost_reasons'"
          >
            Motivos de Perda
          </button>
          <button
            class="px-4 py-4 text-sm font-semibold border-b-2 transition-colors cursor-pointer"
            :class="
              activeTab === 'payment_methods'
                ? 'border-n-brand text-n-brand'
                : 'border-transparent text-n-slate-11 hover:text-slate-800 dark:hover:text-slate-100'
            "
            @click="activeTab = 'payment_methods'"
          >
            Formas de Pagamento
          </button>
          <button
            class="px-4 py-4 text-sm font-semibold border-b-2 transition-colors cursor-pointer"
            :class="
              activeTab === 'origins'
                ? 'border-n-brand text-n-brand'
                : 'border-transparent text-n-slate-11 hover:text-slate-800 dark:hover:text-slate-100'
            "
            @click="activeTab = 'origins'"
          >
            Origens
          </button>
        </div>
      </div>

      <!-- Tab Content: Funnels -->
      <template v-if="activeTab === 'funnels'">
        <!-- List Mode -->
        <template v-if="viewMode === 'list'">
          <div class="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <div
              v-if="funnels.length === 0"
              class="text-sm text-n-slate-11 text-center py-8"
            >
              Você ainda não configurou nenhum funil.
            </div>

            <div
              v-for="f in funnels"
              :key="f.id"
              class="flex items-center justify-between p-3 border border-n-weak rounded-md hover:bg-n-alpha-1 transition-colors"
            >
              <div class="flex items-center gap-3">
                <span class="i-lucide-folder w-5 h-5 text-n-brand" />
                <div>
                  <h3
                    class="text-sm font-semibold m-0 text-slate-800 dark:text-slate-100"
                  >
                    {{ f.name }}
                  </h3>
                  <span class="text-xs text-n-slate-11"
                    >{{ f.columns.length }} etapas</span
                  >
                </div>
              </div>

              <div class="flex items-center gap-2">
                <NextButton
                  v-if="activeFunnelId !== f.id"
                  label="Acessar"
                  size="xs"
                  variant="outline"
                  color="teal"
                  @click="emitSelectFunnel(f.id)"
                />
                <span
                  v-else
                  class="px-2 py-1 text-xs font-semibold rounded-lg text-teal-600 bg-teal-500/10"
                >
                  ✓ Ativo
                </span>
                <NextButton
                  label="Editar"
                  size="xs"
                  variant="outline"
                  color="slate"
                  @click="openEditMode(f)"
                />
                <NextButton
                  label="Excluir"
                  size="xs"
                  variant="outline"
                  color="ruby"
                  @click="deleteFunnel(f.id)"
                />
              </div>
            </div>
          </div>

          <div
            class="px-6 py-4 border-t border-n-weak flex justify-between items-center bg-n-alpha-1"
          >
            <NextButton
              label="Fechar"
              variant="faded"
              color="slate"
              @click="onClose"
            />
            <NextButton
              label="Criar Novo Funil"
              icon="i-lucide-plus"
              @click="openCreateMode"
            />
          </div>
        </template>

        <!-- Form Mode (Edit/Create) -->
        <template v-else>
          <div
            class="px-6 py-4 border-b border-n-weak flex justify-between items-center"
          >
            <div class="flex items-center gap-2">
              <NextButton
                icon="i-lucide-arrow-left"
                variant="ghost"
                color="slate"
                size="sm"
                @click="viewMode = 'list'"
              />
              <h2
                class="text-lg font-semibold m-0 text-slate-800 dark:text-slate-100"
              >
                {{ isEditing ? 'Editar Funil' : 'Criar Novo Funil' }}
              </h2>
            </div>
          </div>

          <div class="flex-1 overflow-y-auto px-6 py-4 space-y-6">
            <div class="mb-4">
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >
                Nome do Funil
              </label>
              <input
                v-model="funnelName"
                type="text"
                class="w-full px-3 py-2 border border-n-weak rounded-md focus:ring-2 focus:ring-n-brand focus:border-n-brand bg-n-surface-1 text-slate-900 dark:text-slate-100"
                placeholder="Ex: Vendas B2B, Suporte Nível 2"
              />
            </div>

            <!-- Modo `deals`: sem etiquetas, as etapas são digitadas pela pessoa -->
            <div v-if="isDealsMode && !isEditing">
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >
                Etapas do Funil
              </label>
              <p class="text-xs text-n-slate-11 mb-3">
                Digite os nomes das etapas do funil, na ordem em que os negócios
                vão avançar.
              </p>

              <div class="space-y-2 mb-4">
                <div
                  v-for="(stage, index) in stageNames"
                  :key="index"
                  class="flex items-center gap-2"
                >
                  <input
                    v-model="stageNames[index]"
                    type="text"
                    class="flex-1 px-3 py-2 border border-n-weak rounded-md focus:ring-2 focus:ring-n-brand focus:border-n-brand bg-n-surface-1 text-slate-900 dark:text-slate-100"
                    placeholder="Ex: Novo, Proposta, Fechado"
                  />
                  <button
                    class="px-2 py-1 text-xs font-semibold rounded bg-red-600 text-white hover:bg-red-700 transition-colors border-none cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    :disabled="stageNames.length <= 1"
                    @click.prevent="removeStageField(index)"
                  >
                    Remover
                  </button>
                </div>
              </div>

              <NextButton
                label="Adicionar Etapa"
                icon="i-lucide-plus"
                size="sm"
                variant="outline"
                color="slate"
                @click="addStageField"
              />
            </div>

            <!-- Modo `deals`, editando: etapas de verdade (com id na API) -->
            <div v-else-if="isDealsMode && isEditing">
              <!-- Requisitos do funil para marcar como ganho -->
              <div
                class="flex items-center gap-2 p-2 mb-4 bg-n-surface-1 border border-n-weak rounded-md"
              >
                <span class="i-lucide-trophy w-4 h-4 text-n-teal-11" />
                <span class="flex-1 text-sm font-medium">
                  Para marcar como ganho
                </span>
                <span
                  v-if="winRequirementCount"
                  class="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-n-brand/10 text-n-brand"
                >
                  {{ winRequirementCount }} requisito{{
                    winRequirementCount > 1 ? 's' : ''
                  }}
                </span>
                <button
                  class="px-2 py-1 text-xs font-semibold rounded bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3 border border-n-weak cursor-pointer transition-colors"
                  @click="showWinRequirements = !showWinRequirements"
                >
                  Requisitos
                </button>
              </div>
              <StageRequirementsPanel
                v-if="showWinRequirements"
                :key="`win-${editingFunnelId}`"
                class="mb-4"
                :funnel-id="editingFunnelId"
                target="win"
                :win-requirements="editingFunnel?.win_requirements || {}"
                @saved="onWinRequirementsSaved"
                @close="showWinRequirements = false"
              />
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >
                Etapas do Funil
              </label>
              <p class="text-xs text-n-slate-11 mb-3">
                Arraste para reordenar. Renomeie ou exclua etapas existentes.
              </p>

              <div
                v-if="editingStages.length === 0"
                class="text-sm text-n-slate-11 text-center py-4"
              >
                Este funil ainda não tem etapas.
              </div>

              <Draggable
                v-model="editingStages"
                class="space-y-2 mb-4"
                ghost-class="opacity-50"
                handle=".drag-handle"
                item-key="id"
                @end="reorderDealStages"
              >
                <template #item="{ element, index }">
                  <div class="space-y-2">
                    <div
                      class="flex items-center gap-2 p-2 bg-n-surface-1 border border-n-weak rounded-md"
                    >
                      <span
                        class="i-lucide-grip-vertical text-n-slate-10 cursor-move drag-handle w-4 h-4"
                      />
                      <template v-if="editingStageIndex !== index">
                        <span class="flex-1 text-sm font-medium">{{
                          element.name
                        }}</span>
                        <span
                          v-if="requirementCount(element)"
                          class="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-n-brand/10 text-n-brand"
                        >
                          {{ requirementCount(element) }} requisito{{
                            requirementCount(element) > 1 ? 's' : ''
                          }}
                        </span>
                        <span
                          v-if="staleLabel(element)"
                          v-tooltip.top="'Destaca negócio parado há mais tempo que isso'"
                          class="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-n-amber-4 text-n-amber-12 flex items-center gap-1"
                        >
                          <span class="i-lucide-alarm-clock w-3 h-3" />
                          {{ staleLabel(element) }}
                        </span>
                        <button
                          class="px-2 py-1 text-xs font-semibold rounded bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3 border border-n-weak cursor-pointer transition-colors"
                          @click="toggleStale(element)"
                        >
                          Prazo
                        </button>
                        <button
                          class="px-2 py-1 text-xs font-semibold rounded bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3 border border-n-weak cursor-pointer transition-colors"
                          @click="toggleRequirements(element)"
                        >
                          Requisitos
                        </button>
                        <button
                          class="px-2 py-1 text-xs font-semibold rounded bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3 border border-n-weak cursor-pointer transition-colors"
                          @click="startEditStage(index)"
                        >
                          Renomear
                        </button>
                        <button
                          class="px-2 py-1 text-xs font-semibold rounded bg-red-600 text-white hover:bg-red-700 transition-colors border-none cursor-pointer"
                          @click="deleteDealStage(element)"
                        >
                          Excluir
                        </button>
                      </template>
                      <template v-else>
                        <input
                          v-model="editingStageValue"
                          type="text"
                          class="flex-1 px-2 py-1 text-sm border border-n-brand rounded-md bg-n-surface-1 text-slate-900 dark:text-slate-100"
                          @keyup.enter="saveEditStage(index)"
                          @keyup.escape="cancelEditStage"
                        />
                        <button
                          class="px-2 py-1 text-xs font-semibold rounded bg-woot-500 text-white hover:bg-woot-600 cursor-pointer transition-colors border-0"
                          @click="saveEditStage(index)"
                        >
                          Salvar
                        </button>
                        <button
                          class="px-2 py-1 text-xs font-semibold rounded bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3 cursor-pointer transition-colors border border-n-weak"
                          @click="cancelEditStage"
                        >
                          Cancelar
                        </button>
                      </template>
                    </div>

                    <div
                      v-if="staleStageId === element.id"
                      class="flex flex-wrap items-center gap-2 p-3 border border-n-amber-6 bg-n-amber-2 rounded-md text-sm text-n-slate-12"
                    >
                      <span class="i-lucide-alarm-clock w-4 h-4 text-n-amber-11" />
                      <span>Destacar negócio parado nesta etapa há mais de</span>
                      <input
                        v-model="staleValue"
                        type="number"
                        min="1"
                        step="1"
                        class="w-16 px-2 py-1 text-sm border border-n-weak rounded-md bg-n-surface-1 text-slate-900 dark:text-slate-100"
                        @keyup.enter="saveStale(element)"
                      />
                      <select
                        v-model="staleUnit"
                        class="px-2 py-1 text-sm border border-n-weak rounded-md bg-n-surface-1 text-slate-900 dark:text-slate-100 cursor-pointer"
                      >
                        <option value="hours">horas</option>
                        <option value="days">dias</option>
                      </select>
                      <button
                        class="px-2 py-1 text-xs font-semibold rounded bg-woot-500 text-white hover:bg-woot-600 cursor-pointer transition-colors border-0"
                        @click="saveStale(element)"
                      >
                        Salvar
                      </button>
                      <button
                        v-if="element.stale_after_hours"
                        class="px-2 py-1 text-xs font-semibold rounded bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3 cursor-pointer transition-colors border border-n-weak"
                        @click="saveStale(element, true)"
                      >
                        Remover destaque
                      </button>
                      <button
                        class="px-2 py-1 text-xs font-semibold rounded bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3 cursor-pointer transition-colors border border-n-weak"
                        @click="staleStageId = null"
                      >
                        Cancelar
                      </button>
                    </div>

                    <StageRequirementsPanel
                      v-if="requirementsStageId === element.id"
                      :key="`req-${element.id}`"
                      :funnel-id="editingFunnelId"
                      :stage="element"
                      @saved="onRequirementsSaved"
                      @close="requirementsStageId = null"
                    />
                  </div>
                </template>
              </Draggable>

              <div class="flex items-center gap-2">
                <input
                  v-model="newStageName"
                  type="text"
                  class="flex-1 px-3 py-2 border border-n-weak rounded-md focus:ring-2 focus:ring-n-brand focus:border-n-brand bg-n-surface-1 text-slate-900 dark:text-slate-100"
                  placeholder="Nome da nova etapa"
                  @keyup.enter="addDealStage"
                />
                <NextButton
                  label="Adicionar Etapa"
                  icon="i-lucide-plus"
                  size="sm"
                  variant="outline"
                  color="slate"
                  @click="addDealStage"
                />
              </div>
            </div>

            <!-- Modo `conversational`: etapas são etiquetas existentes -->
            <div v-else>
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >
                Selecione as Etiquetas (Etapas do Funil)
              </label>
              <p class="text-xs text-n-slate-11 mb-3">
                Escolha as etiquetas que representarão cada coluna neste funil.
                Arraste para reordená-las.
              </p>

              <Draggable
                v-model="selectedLabels"
                class="space-y-2 mb-4"
                ghost-class="opacity-50"
                handle=".drag-handle"
                item-key="id"
              >
                <template #item="{ element }">
                  <div
                    class="flex items-center gap-3 p-2 bg-n-surface-1 border border-n-weak rounded-md"
                  >
                    <span
                      class="i-lucide-grip-vertical text-n-slate-10 cursor-move drag-handle w-4 h-4"
                    />
                    <span
                      class="w-3 h-3 rounded-full flex-shrink-0"
                      :style="{ backgroundColor: element.color || '#ccc' }"
                    />
                    <span class="flex-1 text-sm font-medium">{{
                      element.title
                    }}</span>
                    <button
                      class="px-2 py-1 text-xs font-semibold rounded bg-red-600 text-white hover:bg-red-700 transition-colors border-none cursor-pointer"
                      @click.prevent="removeLabel(element)"
                    >
                      Remover
                    </button>
                  </div>
                </template>
              </Draggable>

              <div v-if="availableLabels.length > 0" class="mt-2">
                <select
                  class="w-full pl-3 pr-6 py-2 border border-n-weak rounded-md bg-n-surface-1 text-slate-900 dark:text-slate-100"
                  @change="addLabel($event)"
                >
                  <option value="" disabled selected>
                    Adicionar uma coluna...
                  </option>
                  <option
                    v-for="label in availableLabels"
                    :key="label.id"
                    :value="label.id"
                  >
                    {{ label.title }}
                  </option>
                </select>
              </div>
              <div v-else class="text-sm text-n-slate-11 italic">
                Todas as etiquetas já foram adicionadas.
              </div>
            </div>
          </div>

          <div
            class="px-6 py-4 border-t border-n-weak flex justify-end items-center gap-2 bg-n-alpha-1"
          >
            <NextButton
              label="Cancelar"
              variant="faded"
              color="slate"
              @click="viewMode = 'list'"
            />
            <NextButton
              :label="isEditing ? 'Salvar Configurações' : 'Criar Funil'"
              :disabled="!isFormValid"
              @click="saveFunnel"
            />
          </div>
        </template>
      </template>

      <!-- Tab Content: List Manager (Lost Reasons / Payment Methods) -->
      <template v-else>
        <div class="flex-1 overflow-y-auto px-6 py-6 flex flex-col gap-4">
          <div class="mb-2">
            <h2
              class="text-lg font-semibold m-0 text-slate-800 dark:text-slate-100 mb-2"
            >
              {{ listConfig.title }}
            </h2>
            <p class="text-sm text-n-slate-11 m-0">
              {{ listConfig.description }}
            </p>
          </div>

          <div class="flex gap-2">
            <input
              v-model="newItemName"
              type="text"
              class="flex-1 px-3 py-2 border border-n-weak rounded-md focus:ring-2 focus:ring-n-brand focus:border-n-brand bg-n-surface-1 text-slate-900 dark:text-slate-100"
              :placeholder="listConfig.placeholder"
              @keyup.enter="addListItem"
            />
            <NextButton
              label="Adicionar"
              icon="i-lucide-plus"
              :disabled="!newItemName.trim()"
              @click="addListItem"
            />
          </div>

          <div
            class="flex-1 min-h-[200px] border border-n-weak rounded-md p-2 bg-n-surface-1 overflow-y-auto"
          >
            <div
              v-if="activeList.length === 0"
              class="text-sm text-n-slate-11 text-center py-8"
            >
              Nenhum item configurado.
            </div>

            <Draggable
              v-model="activeList"
              class="space-y-2"
              ghost-class="opacity-50"
              handle=".drag-handle"
              item-key="index"
              :disabled="isDealsMode"
              @change="hasChanges = true"
            >
              <template #item="{ element, index }">
                <div
                  class="flex items-center gap-2 p-3 bg-white dark:bg-slate-900 border border-n-weak rounded-md hover:border-n-strong transition-colors group"
                >
                  <span
                    v-if="!isDealsMode"
                    class="i-lucide-grip-vertical text-n-slate-10 cursor-move drag-handle w-4 h-4 flex-shrink-0"
                  />
                  <!-- View mode -->
                  <template v-if="editingIndex !== index">
                    <span
                      class="flex-1 text-sm font-medium text-slate-800 dark:text-slate-100"
                      >{{ isDealsMode ? element.name : element }}</span
                    >
                    <button
                      class="px-2 py-1 text-xs font-semibold rounded bg-transparent text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-brand border border-transparent hover:border-n-weak cursor-pointer transition-colors"
                      title="Editar"
                      @click="startEdit(index)"
                    >
                      ✏️
                    </button>
                    <button
                      class="px-2 py-1 text-xs font-semibold rounded bg-transparent text-n-slate-11 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 border border-transparent hover:border-red-200 cursor-pointer transition-colors"
                      title="Excluir"
                      @click="removeListItem(index)"
                    >
                      🗑️
                    </button>
                  </template>
                  <!-- Edit mode -->
                  <template v-else>
                    <input
                      v-model="editingValue"
                      type="text"
                      class="flex-1 px-2 py-1 text-sm border border-n-brand rounded-md bg-n-surface-1 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-n-brand"
                      @keyup.enter="saveEdit(index)"
                      @keyup.escape="cancelEdit"
                    />
                    <button
                      class="px-2 py-1 text-xs font-semibold rounded bg-woot-500 text-white hover:bg-woot-600 cursor-pointer transition-colors border-0"
                      @click="saveEdit(index)"
                    >
                      Salvar
                    </button>
                    <button
                      class="px-2 py-1 text-xs font-semibold rounded bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3 cursor-pointer transition-colors border border-n-weak"
                      @click="cancelEdit"
                    >
                      Cancelar
                    </button>
                  </template>
                </div>
              </template>
            </Draggable>
          </div>
        </div>

        <div
          class="px-6 py-4 border-t border-n-weak flex justify-between items-center bg-n-alpha-1"
        >
          <div class="text-sm text-n-slate-11">
            <span v-if="hasChanges" class="text-amber-500 font-medium"
              >Alterações não salvas</span
            >
          </div>
          <div class="flex gap-2">
            <NextButton
              label="Fechar"
              variant="faded"
              color="slate"
              @click="onClose"
            />
            <NextButton
              v-if="!isDealsMode"
              label="Salvar Alterações"
              :disabled="!hasChanges"
              @click="saveList"
            />
          </div>
        </div>
      </template>
    </div>
  </woot-modal>
</template>
